import { Link } from "wouter";
import { MapPin, Clock, Users, Gavel, Briefcase, ReceiptText, Package, Boxes, Wrench, ImageOff, Star, Check } from "lucide-react";
import { Avatar, Badge, Rating, Verified } from "./ui/kit";
import { SaveButton } from "./SaveButton";
import { peso, type Person, type Service, type Company, type Job, type Project, type Rfq } from "../data/mock";

const saveChip = "absolute top-3 right-3 h-8 w-8 bg-white/90 shadow-sm z-10";

export function PersonCard({ p }: { p: Person }) {
  const media = (p as any).mediaUrl as string | null; const isStar = !!(p as any).isStar;
  return (
    <div className="card p-4 flex flex-col gap-3 hover:shadow-lift transition-shadow relative">
      <SaveButton kind="person" refId={p.id} className={saveChip} />
      {media && <div className="-mx-4 -mt-4 mb-1 h-32 overflow-hidden rounded-t-xl bg-navy-50"><img src={media} alt="" className="w-full h-full object-cover" loading="lazy" /></div>}
      <div className="flex items-start gap-3">
        <Avatar initials={p.initials} />
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2 flex-wrap">
            <h3 className="font-bold text-navy truncate">{p.name}</h3>
            {p.verified && <Verified label="Verified ID" />}
            {isStar && <span className="chip bg-brand-gold/15 text-[#8a5a00] text-[10px] font-bold"><Star className="w-3 h-3 fill-brand-gold text-brand-gold" />Star</span>}
          </div>
          <p className="text-sm text-mut">{p.title}</p>
          <p className="text-xs text-mut flex items-center gap-1 mt-0.5"><MapPin className="w-3 h-3" />{p.city} · {p.years} yrs exp</p>
        </div>
      </div>
      <div className="flex flex-wrap gap-1.5">{(p.skills ?? []).slice(0, 3).map((s) => <Badge key={s}>{s}</Badge>)}</div>
      <div className="flex items-center justify-between">
        <Rating value={p.rating} count={p.reviews} />
        {p.available && <span className="text-xs font-semibold text-brand-green">● Available Now</span>}
      </div>
      <div className="flex items-center justify-between pt-1 border-t border-line">
        <span className="text-sm font-bold text-navy">{p.rate}</span>
        <Link href={`/p/${p.id}`} className="btn-outline h-9 px-3 text-xs">View Résumé</Link>
      </div>
    </div>
  );
}

export function ServiceCard({ s }: { s: Service }) {
  return (
    <div className="card p-4 flex flex-col gap-3 hover:shadow-lift transition-shadow relative">
      <SaveButton kind="service" refId={s.id} className={saveChip} />
      <div className="flex items-center gap-2 flex-wrap">
        <Link href={`/s/${s.id}`} className="font-bold text-navy hover:text-brand-red">{s.title}</Link>
        {s.verified && <Verified label={s.company ? "Verified Company" : "Verified"} />}
      </div>
      <p className="text-sm text-mut">by {s.by}</p>
      <p className="text-xs text-mut flex items-center gap-1"><MapPin className="w-3 h-3" />{s.city}</p>
      <div className="flex flex-wrap gap-1.5">{(s.tags ?? []).map((t) => <Badge key={t}>{t}</Badge>)}</div>
      <div className="flex items-center justify-between pt-1 border-t border-line">
        <div>
          <span className="text-xs text-mut">Starting at</span>
          <div className="text-sm font-bold text-navy">{peso(s.from)}</div>
        </div>
        <div className="flex items-center gap-2">
          <Rating value={s.rating} count={s.reviews} />
          <Link href={`/s/${s.id}`} className="btn-red h-9 px-3 text-xs">Request Quote</Link>
        </div>
      </div>
    </div>
  );
}

export function CompanyCard({ c }: { c: Company }) {
  return (
    <div className="card p-4 flex flex-col gap-3 hover:shadow-lift transition-shadow relative">
      <SaveButton kind="company" refId={c.id} className={saveChip} />
      <div className="flex items-start gap-3">
        <div className="w-11 h-11 rounded-lg bg-navy text-white font-bold grid place-items-center shrink-0">{c.initials}</div>
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2 flex-wrap">
            <h3 className="font-bold text-navy truncate">{c.name}</h3>
            {c.verified && <Verified label="Verified Company" />}
          </div>
          <p className="text-sm text-mut truncate">{c.tagline}</p>
          <p className="text-xs text-mut flex items-center gap-1 mt-0.5"><MapPin className="w-3 h-3" />{c.city} · since {c.since}</p>
        </div>
      </div>
      <div className="flex flex-wrap gap-1.5">{(c.tags ?? []).map((t) => <Badge key={t}>{t}</Badge>)}</div>
      <div className="flex items-center justify-between pt-1 border-t border-line">
        <Rating value={c.rating} count={c.reviews} />
        <Link href={`/c/${c.id}`} className="btn-outline h-9 px-3 text-xs">View Company</Link>
      </div>
    </div>
  );
}

export function JobCard({ j, applied = false }: { j: Job; applied?: boolean }) {
  return (
    <div className="card p-4 flex flex-col gap-3 hover:shadow-lift transition-shadow relative">
      <SaveButton kind="job" refId={j.id} className={saveChip} />
      <div className="flex items-start gap-3">
        <div className="w-11 h-11 rounded-lg bg-navy-50 text-navy grid place-items-center shrink-0"><Briefcase className="w-5 h-5" /></div>
        <div className="min-w-0 flex-1">
          <Link href={`/j/${j.id}`} className="font-bold text-navy hover:text-brand-red">{j.title}</Link>
          <p className="text-sm text-mut">{j.company}</p>
          <p className="text-xs text-mut flex items-center gap-1 mt-0.5"><MapPin className="w-3 h-3" />{j.city} · {j.type}</p>
        </div>
      </div>
      <div className="flex flex-wrap gap-1.5">{(j.tags ?? []).map((t) => <Badge key={t}>{t}</Badge>)}</div>
      <div className="flex items-center gap-4 text-xs text-mut">
        <span className="flex items-center gap-1"><Users className="w-3 h-3" />{j.applicants} applicant{j.applicants === 1 ? "" : "s"}</span>
        <span className="flex items-center gap-1"><Clock className="w-3 h-3" />{j.posted}</span>
      </div>
      <div className="flex items-center justify-between pt-1 border-t border-line">
        <div>
          <span className="text-xs text-mut">Salary</span>
          <div className="text-sm font-bold text-navy">{j.salary}</div>
        </div>
        {/* QA #22: reflect that the user has already applied. */}
        {applied
          ? <Link href={`/j/${j.id}`} className="h-9 px-4 text-xs inline-flex items-center gap-1 rounded-lg bg-brand-green/10 text-brand-green font-semibold border border-brand-green/30"><Check className="w-3.5 h-3.5" />Applied</Link>
          : <Link href={`/j/${j.id}`} className="btn-red h-9 px-4 text-xs">Apply Now</Link>}
      </div>
    </div>
  );
}

export function ProjectCard({ p }: { p: Project }) {
  return (
    <div className="card p-4 flex flex-col gap-3 hover:shadow-lift transition-shadow relative">
      <SaveButton kind="project" refId={p.id} className={saveChip} />
      <div className="flex items-start justify-between gap-2">
        <div>
          <span className="chip bg-brand-gold/15 text-brand-gold mb-1">{p.bids} Bids</span>
          <h3 className="font-bold text-navy">{p.title}</h3>
        </div>
      </div>
      <p className="text-xs text-mut flex items-center gap-1"><MapPin className="w-3 h-3" />{p.location} · {p.category}</p>
      <div className="flex items-center gap-4 text-xs text-mut">
        <span className="flex items-center gap-1"><Clock className="w-3 h-3" />Deadline: {p.deadline}</span>
      </div>
      <div className="flex items-center justify-between pt-1 border-t border-line">
        <div>
          <span className="text-xs text-mut">{p.budgetType} Budget</span>
          <div className="text-sm font-bold text-navy">{peso(p.budget)}</div>
        </div>
        <Link href={`/project/${p.id}`} className="btn-navy h-9 px-4 text-xs"><Gavel className="w-3.5 h-3.5" />Submit Bid</Link>
      </div>
    </div>
  );
}

export function RfqCard({ r }: { r: Rfq }) {
  const items = Array.isArray(r.items) ? r.items : [];
  return (
    <div className="card p-4 flex flex-col gap-3 hover:shadow-lift transition-shadow relative">
      <SaveButton kind="rfq" refId={r.id} className={saveChip} />
      <div className="flex items-start justify-between gap-2">
        <div>
          <span className="chip bg-brand-green/10 text-brand-greendark mb-1">{r.quotes} Quote{r.quotes === 1 ? "" : "s"}</span>
          <h3 className="font-bold text-navy">{r.title}</h3>
        </div>
        <ReceiptText className="w-5 h-5 text-navy shrink-0" />
      </div>
      <p className="text-xs text-mut flex items-center gap-1"><MapPin className="w-3 h-3" />{r.location} · {r.category}</p>
      <div className="flex items-center gap-4 text-xs text-mut">
        <span className="flex items-center gap-1"><Package className="w-3 h-3" />{items.length} line item{items.length === 1 ? "" : "s"}</span>
        <span className="flex items-center gap-1"><Clock className="w-3 h-3" />Due: {r.deadline}</span>
      </div>
      <div className="flex items-center justify-between pt-1 border-t border-line">
        <span className="text-xs text-mut truncate max-w-[55%]">{items.slice(0, 2).map((i) => i.name).join(", ")}{items.length > 2 ? "…" : ""}</span>
        <Link href={`/rfq/${r.id}`} className="btn-navy h-9 px-4 text-xs"><ReceiptText className="w-3.5 h-3.5" />Submit Quote</Link>
      </div>
    </div>
  );
}

export function ItemCard({ it }: { it: any }) {
  const isRent = it.mode === "rent";
  const isEquip = it.kind === "equipment";
  const tags = Array.isArray(it.tags) ? it.tags : [];
  const inStock = (it.stock ?? 0) > 0;
  return (
    <div className="card overflow-hidden flex flex-col hover:shadow-lift transition-shadow relative">
      <SaveButton kind="item" refId={it.id} className={saveChip} />
      <Link href={`/item/${it.id}`} className="block">
        <div className="aspect-[4/3] bg-navy-50 grid place-items-center overflow-hidden">
          {it.imageUrl
            ? <img src={it.imageUrl} alt={it.title} className="w-full h-full object-cover" loading="lazy" />
            : <div className="text-mut flex flex-col items-center gap-1"><ImageOff className="w-7 h-7" /><span className="text-[11px]">No photo</span></div>}
        </div>
      </Link>
      <div className="p-4 flex flex-col gap-2 flex-1">
        <div className="flex items-center gap-1.5 flex-wrap">
          <span className={`chip ${isEquip ? "bg-brand-gold/15 text-brand-gold" : "bg-navy-50 text-navy-600"}`}>
            {isEquip ? <Wrench className="w-3 h-3" /> : <Boxes className="w-3 h-3" />}{isEquip ? "Equipment" : "Material"}
          </span>
          <span className={`chip ${isRent ? "bg-brand-green/10 text-brand-greendark" : "bg-navy-50 text-navy-600"}`}>{isRent ? "For Rent" : "For Sale"}</span>
          {it.condition && <span className="chip bg-navy-50 text-navy-600 capitalize">{it.condition}</span>}
        </div>
        <Link href={`/item/${it.id}`} className="font-bold text-navy hover:text-brand-red leading-snug line-clamp-2">{it.title}</Link>
        <div className="flex items-center gap-2 text-xs text-mut">
          <span className="flex items-center gap-1"><MapPin className="w-3 h-3" />{it.location || "—"}{it.category ? ` · ${it.category}` : ""}</span>
          {it.sellerRating?.count > 0 && <span className="inline-flex items-center gap-0.5 text-navy"><Star className="w-3 h-3 fill-brand-gold text-brand-gold" /><b>{it.sellerRating.average.toFixed(1)}</b></span>}
        </div>
        {tags.length > 0 && <div className="flex flex-wrap gap-1.5">{tags.slice(0, 3).map((t: string) => <Badge key={t}>{t}</Badge>)}</div>}
        <div className="flex items-center justify-between pt-2 mt-auto border-t border-line">
          <div>
            <div className="text-sm font-bold text-navy">{peso(it.price)}{isRent ? <span className="text-xs font-normal text-mut">/{it.unit || "day"}</span> : ""}</div>
            <span className={`text-[11px] ${inStock ? "text-brand-green" : "text-brand-red"}`}>{inStock ? `${it.stock} ${it.unit || "in stock"}` : "Out of stock"}</span>
          </div>
          <Link href={`/item/${it.id}`} className="btn-navy h-9 px-4 text-xs"><Package className="w-3.5 h-3.5" />{isRent ? "Rent" : "Buy"}</Link>
        </div>
      </div>
    </div>
  );
}
